import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DatePicker } from '@/components/composite/date-picker/date-picker';
import { StatusBadge } from '@/components/composite/status-badge/status-badge';
import { todayIsoDate, yesterdayIsoDate } from '@/lib/datetime';
import type { BatchAccountStatusResult } from '@/lib/api/accounts';
import type { AdsAccount } from '@/lib/api/types';

export type BatchStatusAction = 'ban' | 'zero';

interface BatchStatusDialogProps {
  action: BatchStatusAction | null;
  accounts: AdsAccount[];
  isPending: boolean;
  error: Error | null;
  result: BatchAccountStatusResult | null;
  onClose: () => void;
  onConfirm: (accountIds: string[], banDate?: string) => void;
}

const COPY = {
  ban: { title: '确认批量封禁？', verb: '封禁' },
  zero: { title: '确认批量清零？', verb: '清零' },
} as const;

/** 状态不符的账户提交上去必然失败，这里先分出来并跳过，结果弹窗才不会全是噪音错误。 */
function skipReason(action: BatchStatusAction, status: AdsAccount['status']): string | null {
  if (action === 'ban') {
    if (status === 1) return '已封禁';
    if (status === 2) return '已清零';
    return null;
  }
  if (status === 0) return '未封禁';
  if (status === 2) return '已清零';
  return null;
}

export function BatchStatusDialog({
  action,
  accounts,
  isPending,
  error,
  result,
  onClose,
  onConfirm,
}: BatchStatusDialogProps) {
  const [banDate, setBanDate] = useState(todayIsoDate());

  useEffect(() => {
    if (action) setBanDate(todayIsoDate());
  }, [action]);

  if (!action) return null;

  const { title, verb } = COPY[action];
  const rows = accounts.map((account) => ({
    account,
    skipped: skipReason(action, account.status),
  }));
  const eligible = rows.filter((r) => !r.skipped).map((r) => r.account);
  const skippedCount = rows.length - eligible.length;
  const failedAccountCode = (accountId: string) =>
    accounts.find((a) => a.accountId === accountId)?.accountCode ?? accountId;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[520px]">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>批量{verb}结果</DialogTitle>
              <DialogDescription>
                成功 {result.succeeded.length} 个，失败 {result.errors.length}{' '}
                个。失败的账户仍保持选中，可修正后重试。
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-64">
              <ul className="space-y-1.5 pr-3">
                {result.errors.map((item) => (
                  <li
                    key={item.accountId}
                    className="flex items-baseline justify-between gap-3 text-sm"
                  >
                    <span className="font-mono">{failedAccountCode(item.accountId)}</span>
                    <span className="text-right text-destructive">{item.reason}</span>
                  </li>
                ))}
              </ul>
            </ScrollArea>
            <DialogFooter>
              <Button onClick={onClose}>关闭</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                本次将{verb} {eligible.length} 个账户
                {skippedCount > 0 && `（${skippedCount} 个状态不符，将被跳过）`}。
              </DialogDescription>
            </DialogHeader>

            {action === 'ban' && (
              <div className="space-y-2">
                <Label htmlFor="batch-ban-date">封禁日期</Label>
                <div className="flex items-center gap-1.5">
                  <DatePicker
                    value={banDate}
                    onChange={setBanDate}
                    max={todayIsoDate()}
                    className="flex-1"
                  />
                  {(
                    [
                      ['昨日', yesterdayIsoDate()],
                      ['今日', todayIsoDate()],
                    ] as [string, string][]
                  ).map(([label, value]) => (
                    <Button
                      key={label}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9"
                      onClick={() => setBanDate(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <ScrollArea className="max-h-48">
              {/* li 用 display:contents 交出布局权，让账户号/状态/跳过三列跨行对齐 */}
              <ul
                className={`grid items-center gap-x-3 gap-y-1.5 pr-3 ${
                  skippedCount > 0
                    ? 'grid-cols-[minmax(0,1fr)_auto_auto]'
                    : 'grid-cols-[minmax(0,1fr)_auto]'
                }`}
              >
                {rows.map(({ account, skipped }) => (
                  <li
                    key={account.accountId}
                    className={`contents ${skipped ? 'text-muted-foreground' : ''}`}
                  >
                    <span className="truncate font-mono text-sm">{account.accountCode}</span>
                    <StatusBadge status={account.status} className="justify-self-start" />
                    {skippedCount > 0 && (
                      <span className="text-xs text-muted-foreground">{skipped ? '跳过' : ''}</span>
                    )}
                  </li>
                ))}
              </ul>
            </ScrollArea>

            {error && (
              <div
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error.message}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isPending}>
                取消
              </Button>
              <Button
                variant="destructive"
                disabled={eligible.length === 0 || isPending}
                onClick={() =>
                  onConfirm(
                    eligible.map((a) => a.accountId),
                    action === 'ban' ? banDate : undefined,
                  )
                }
              >
                批量{verb}（{eligible.length}）
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
